import { karteFuer } from '../karten/karten'
import type { KartenId } from '../karten/typen'
import type { IsoDatum, Kartenstand } from '../scheduler/typen'
import { tageZwischen } from '../scheduler/datum'
import type { GespeicherteAntwort } from './typen'

/**
 * Persönliche Fortschritts-Momente aus der Antwort-Historie.
 *
 * Das ist der Grund, warum jede einzelne Antwort mit Millisekunden gespeichert
 * wird. Ein Kind glaubt "du wirst besser" nicht, weil eine App es behauptet,
 * sondern weil es den Beweis sieht: dieselbe Aufgabe, die vor zwei Wochen neun
 * Sekunden gedauert hat, geht heute in zwei.
 *
 * Genau das ist der Unterschied zwischen einem verdienten Kompliment und
 * einer Punktzahl. Punkte sind ausgedacht, dieser Satz ist wahr und
 * nachprüfbar. Deshalb ist er das einzige Lob in der App, das eine Zahl nennt.
 */

/** Mindestens so viel schneller, damit es sich zu erwähnen lohnt. */
const MIN_VERBESSERUNG_MS = 1500

/** Der frühere Versuch muss mindestens so lange her sein. */
const MIN_TAGE_ABSTAND = 2

export interface Fortschrittsmoment {
  karte: KartenId
  /** Anzeigetext der Karte, etwa "7 × 8". */
  text: string
  frueherMs: number
  jetztMs: number
  tage: number
}

/**
 * Die schönste Verbesserung des heutigen Tages.
 *
 * Gesucht wird eine Karte, die heute sicher saß und früher einmal deutlich
 * länger gedauert hat. Gibt es keine, kommt null zurück und die Oberfläche
 * zeigt schlicht nichts. Lieber kein Lob als ein erfundenes.
 */
export function besterMoment(
  antworten: GespeicherteAntwort[],
  heute: IsoDatum,
): Fortschrittsmoment | null {
  const heutige = antworten.filter(
    (a) => a.erstelltAm.slice(0, 10) === heute && a.richtig && a.zone === 'gewusst',
  )
  if (heutige.length === 0) return null

  // Je Karte die beste Zeit von heute. Hat sie eine Karte heute zweimal
  // gehabt, zählt der bessere Versuch.
  const heuteBeste = new Map<KartenId, number>()
  for (const a of heutige) {
    const bisher = heuteBeste.get(a.karte)
    if (bisher === undefined || a.zeitMs < bisher) heuteBeste.set(a.karte, a.zeitMs)
  }

  let bester: Fortschrittsmoment | null = null

  for (const [karte, jetztMs] of heuteBeste) {
    // Der langsamste frühere Versuch, der weit genug zurückliegt. Der
    // langsamste deshalb, weil genau er den Weg zeigt, den sie gegangen ist.
    let frueherMs = 0
    let frueherDatum = ''

    for (const a of antworten) {
      if (a.karte !== karte) continue
      const datum = a.erstelltAm.slice(0, 10)
      if (datum >= heute) continue
      if (tageZwischen(datum, heute) < MIN_TAGE_ABSTAND) continue
      if (a.zeitMs > frueherMs) {
        frueherMs = a.zeitMs
        frueherDatum = datum
      }
    }

    if (!frueherDatum) continue
    if (frueherMs - jetztMs < MIN_VERBESSERUNG_MS) continue

    const kandidat: Fortschrittsmoment = {
      karte,
      text: kartentext(karte),
      frueherMs,
      jetztMs,
      tage: tageZwischen(frueherDatum, heute),
    }

    if (!bester || kandidat.frueherMs - kandidat.jetztMs > bester.frueherMs - bester.jetztMs) {
      bester = kandidat
    }
  }

  return bester
}

function kartentext(karte: KartenId): string {
  try {
    const k = karteFuer(karte)
    return `${k.a} × ${k.b}`
  } catch {
    return karte.replace('x', ' × ')
  }
}

/** Ab diesem Fach gilt eine Karte als sicher. */
export const SICHER_AB_FACH = 4

export function anzahlSicher(staende: Kartenstand[]): number {
  return staende.filter((s) => s.fach >= SICHER_AB_FACH).length
}

/**
 * Wie viele Karten durch diese Runde neu in den sicheren Bereich gerutscht sind.
 *
 * Wird mit den Ständen von vor der Runde verglichen, damit der Satz "zwei
 * Aufgaben sitzen jetzt sicher" auch wirklich für heute gilt.
 */
export function neuSicher(vorher: Kartenstand[], nachher: Kartenstand[]): number {
  const vorherSicher = new Set(
    vorher.filter((s) => s.fach >= SICHER_AB_FACH).map((s) => s.karte),
  )
  return nachher.filter((s) => s.fach >= SICHER_AB_FACH && !vorherSicher.has(s.karte)).length
}

/**
 * Ein Satz für die Abschlussseite, oder null.
 *
 * Bewusst immer nur EIN Satz. Drei Glückwünsche nebeneinander heben sich
 * gegenseitig auf, und das Kind liest dann keinen einzigen.
 */
export function fortschrittssatz(moment: Fortschrittsmoment | null, neueSichere: number): string | null {
  if (moment) {
    const frueher = (moment.frueherMs / 1000).toFixed(0)
    const jetzt = (moment.jetztMs / 1000).toFixed(1)
    const wann = moment.tage < 7 ? `vor ${moment.tage} Tagen` : 'am Anfang'
    return `${moment.text} ging heute in ${jetzt} Sekunden. ${wann} hast du dafür noch ${frueher} gebraucht!`
  }
  if (neueSichere === 1) return 'Eine Aufgabe sitzt jetzt richtig sicher.'
  if (neueSichere > 1) return `${neueSichere} Aufgaben sitzen jetzt richtig sicher.`
  return null
}
